import { useDataStore } from '../stores/dataStore.js';
import { apiClient } from '../services/apiClient.js';
import { searchService } from '../services/searchService.js';
import { useSearchStore } from '../stores/searchStore.js';

export class SearchBar {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      placeholder: 'Search mines, commodities, countries...',
      debounceMs: 500,
      maxSuggestions: 10,
      minSearchLength: 2,
      enableSuggestions: true,
      ...options
    };
    this.isVisible = false;
    this.currentQuery = '';
    this.suggestions = [];
    this.selectedIndex = -1;
    this.debounceTimer = null;
    this.storeUnsubscribe = null;
    this.boundEscapeHandler = null;
    this.init();
  }

  init() {
    this.render();
    this.attachEventListeners();
    this.setupStoreSubscription();
  }

  setupStoreSubscription() {
    console.log('SearchBar: Setting up store subscription');

    // Subscribe to search store changes - use the direct callback format
    this.storeUnsubscribe = useSearchStore.subscribe((state) => {
      console.log('SearchBar: Store subscription callback triggered, state results length:', state.results?.length || 0);

      // Only update if this is for the current query
      console.log('SearchBar: Current query:', this.currentQuery, 'Store query:', state.query);

      if (state.query === this.currentQuery) {
        this.suggestions = state.results || [];
        this.selectedIndex = -1;

        console.log('SearchBar: Updating suggestions with', this.suggestions.length, 'items');

        if (this.suggestions.length > 0) {
          console.log('SearchBar: Store subscription - showing suggestions');
          this.renderSuggestions();
          this.showSuggestions();
        } else {
          console.log('SearchBar: Store subscription - hiding suggestions (no results)');
          this.hideSuggestions();
        }
      } else {
        console.log('SearchBar: Query mismatch, not updating suggestions');
      }
    });
  }

  render() {
    // The search container already exists in HTML, we just need to manage its behavior
    this.searchInput = document.getElementById('searchInput');
    this.searchSuggestions = document.getElementById('searchSuggestions');
    this.searchClose = document.getElementById('searchClose');

    if (this.searchInput) {
      this.searchInput.placeholder = this.options.placeholder;
    }
  }

  attachEventListeners() {
    if (!this.searchInput) return;

    // Input event with debouncing
    this.searchInput.addEventListener('input', (e) => {
      this.handleInput(e.target.value);
    });

    // Keyboard navigation
    this.searchInput.addEventListener('keydown', (e) => {
      this.handleKeyDown(e);
    });

    // Focus events
    this.searchInput.addEventListener('focus', () => {
      this.handleFocus();
    });

    this.searchInput.addEventListener('blur', (e) => {
      // Delay blur to allow clicking on suggestions
      setTimeout(() => {
        if (!this.container.querySelector(':hover')) {
          this.hideSuggestions();
        }
      }, 150);
    });

    // Close button
    if (this.searchClose) {
      this.searchClose.addEventListener('click', () => {
        this.clearSearch();
      });
    }

    // Suggestions click handling
    if (this.searchSuggestions) {
      this.searchSuggestions.addEventListener('click', (e) => {
        this.handleSuggestionClick(e);
      });
    }

    // Search toggle button
    const searchToggle = document.getElementById('searchToggle');
    if (searchToggle) {
      searchToggle.addEventListener('click', () => {
        this.toggle();
      });
    }

    // ESC key to close - store bound handler for cleanup
    this.boundEscapeHandler = (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hide();
      }
    };
    document.addEventListener('keydown', this.boundEscapeHandler);
  }

  handleInput(value) {
    this.currentQuery = value;

    // Clear previous debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Update close button visibility
    this.updateCloseButton();

    if (value.length >= this.options.minSearchLength) {
      // Debounce both search and suggestions
      this.debounceTimer = setTimeout(() => {
        if (this.options.enableSuggestions) {
          this.loadSuggestions(value);
        }
        // performSearch is handled by the user pressing enter or selecting a suggestion
      }, this.options.debounceMs);
    } else {
      this.hideSuggestions();
    }
  }

  handleKeyDown(e) {
    if (!this.options.enableSuggestions || this.suggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.performSearch(this.currentQuery);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.suggestions.length - 1);
        this.updateSuggestionHighlight();
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSuggestionHighlight();
        break;

      case 'Enter':
        e.preventDefault();
        if (this.selectedIndex >= 0 && this.suggestions[this.selectedIndex]) {
          this.selectSuggestion(this.suggestions[this.selectedIndex]);
        } else {
          this.performSearch(this.currentQuery);
        }
        break;

      case 'Escape':
        this.hideSuggestions();
        this.searchInput.blur();
        break;
    }
  }

  handleFocus() {
    if (this.currentQuery.length >= this.options.minSearchLength && this.suggestions.length > 0) {
      this.showSuggestions();
    }
  }

  handleSuggestionClick(e) {
    const suggestionElement = e.target.closest('.search-suggestion');
    if (suggestionElement) {
      const index = parseInt(suggestionElement.dataset.index);
      if (this.suggestions[index]) {
        this.selectSuggestion(this.suggestions[index]);
      }
    }
  }

  async loadSuggestions(query) {
    try {
      console.log('SearchBar: Loading suggestions for query:', query);

      // Just trigger the search - the store subscription will handle the UI update
      searchService.executeSearch(query, true);

    } catch (error) {
      console.error('Failed to load search suggestions:', error);
      this.suggestions = [];
      this.hideSuggestions();
    }
  }

  renderSuggestions() {
    console.log('SearchBar: renderSuggestions called with', this.suggestions.length, 'suggestions');

    if (!this.searchSuggestions) {
      console.error('SearchBar: searchSuggestions element not found');
      return;
    }

    if (!Array.isArray(this.suggestions)) {
      console.error('SearchBar: suggestions is not an array:', this.suggestions);
      return;
    }

    this.searchSuggestions.innerHTML = this.suggestions.map((suggestion, index) => {
      // Defensive checks for suggestion object
      const title = suggestion?.title || 'Unknown';
      const type = suggestion?.type || 'mine';
      const subtitle = suggestion?.subtitle || '';

      return `
        <div class="search-suggestion" data-index="${index}" data-suggestion='${JSON.stringify(suggestion)}'>
          <div class="suggestion-icon">
            <i class="fas ${this.getSuggestionIcon(type)}"></i>
          </div>
          <div class="suggestion-content">
            <div class="suggestion-title">${this.highlightMatch(title, this.currentQuery)}</div>
            ${subtitle ? `<div class="suggestion-subtitle">${this.highlightMatch(subtitle, this.currentQuery)}</div>` : ''}
            <div class="suggestion-type">${type}</div>
          </div>
        </div>
      `;
    }).join('');
  }

  getSuggestionIcon(type) {
    const icons = {
      mine: 'fa-mountain',
      commodity: 'fa-gem',
      country: 'fa-flag',
      company: 'fa-building'
    };
    return icons[type] || 'fa-search';
  }

  highlightMatch(text, query) {
    if (!text || !query) return text || '';
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  attachSuggestionEventListeners() {
    // This method is no longer needed since we handle clicks via event delegation
    // in the main event listener
  }

  updateSuggestionHighlight() {
    if (!this.searchSuggestions) return;

    const suggestions = this.searchSuggestions.querySelectorAll('.search-suggestion');
    suggestions.forEach((suggestion, index) => {
      suggestion.classList.toggle('highlighted', index === this.selectedIndex);
    });
  }

  async selectSuggestion(suggestion) {
    try {
      console.log('SearchBar: Suggestion selected', suggestion);

      // Use searchService to handle selection
      await searchService.selectResult(suggestion, {
        centerMap: true,
        applyFilters: true,
        closeSearch: true
      });

      // Update search input with selected title
      this.searchInput.value = suggestion.title;
      this.currentQuery = suggestion.title;
      this.hideSuggestions();

    } catch (error) {
      console.error('Failed to select suggestion:', error);
    }
  }

  async performSearch(query, suggestion = null) {
    if (!query.trim()) return;

    try {
      // Show loading state
      this.setLoading(true);

      // Perform search through data store
      const dataStore = useDataStore.getState();
      await dataStore.searchMines(query, {
        type: suggestion?.type,
        id: suggestion?.id
      });

      // Dispatch search event
      const event = new CustomEvent('searchPerformed', {
        detail: {
          query,
          suggestion,
          timestamp: new Date()
        }
      });
      this.container.dispatchEvent(event);

    } catch (error) {
      console.error('Search failed:', error);

      // Show error notification
      const event = new CustomEvent('searchError', {
        detail: {
          query,
          error: error.message
        }
      });
      document.dispatchEvent(event);
    } finally {
      this.setLoading(false);
    }
  }

  showSuggestions() {
    console.log('SearchBar: showSuggestions called');
    if (this.searchSuggestions) {
      console.log('SearchBar: Adding visible class to suggestions');
      this.searchSuggestions.classList.add('visible');
    } else {
      console.error('SearchBar: searchSuggestions element not found for showing');
    }
  }

  hideSuggestions() {
    if (this.searchSuggestions) {
      this.searchSuggestions.classList.remove('visible');
    }
    this.selectedIndex = -1;
  }

  show() {
    this.isVisible = true;
    this.container.classList.add('visible');

    // Focus the input
    if (this.searchInput) {
      setTimeout(() => {
        this.searchInput.focus();
      }, 100);
    }
  }

  hide() {
    this.isVisible = false;
    this.container.classList.remove('visible');
    this.hideSuggestions();
  }

  toggle() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  clearSearch() {
    this.searchInput.value = '';
    this.currentQuery = '';
    this.hideSuggestions();
    this.updateCloseButton();

    // Clear search results
    const dataStore = useDataStore.getState();
    dataStore.clearSearch();

    // Dispatch clear event
    const event = new CustomEvent('searchCleared');
    this.container.dispatchEvent(event);
  }

  updateCloseButton() {
    if (this.searchClose) {
      this.searchClose.style.display = this.currentQuery ? 'block' : 'none';
    }
  }

  setLoading(loading) {
    if (this.searchInput) {
      this.searchInput.disabled = loading;

      // Add loading indicator
      const container = this.searchInput.parentElement;
      if (loading) {
        container.classList.add('loading');
      } else {
        container.classList.remove('loading');
      }
    }
  }

  getCurrentQuery() {
    return this.currentQuery;
  }

  destroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Unsubscribe from store
    if (this.storeUnsubscribe) {
      this.storeUnsubscribe();
    }

    // Remove document event listener to prevent memory leak
    if (this.boundEscapeHandler) {
      document.removeEventListener('keydown', this.boundEscapeHandler);
      this.boundEscapeHandler = null;
    }

    this.hideSuggestions();
    this.currentQuery = '';
    this.suggestions = [];
  }
}